import { TableFilterConfig } from '@/components/data-table/filters-v2';
import i18n from '@/core/i18n';
import { DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES } from '@/lib/constants/pagination';
import { EntityAvatar } from '@/modules/common';
import { ProjectBillingModel, ProjectStatus } from '@/modules/projects/types/project';
import { getShortTeamMembers } from '@/modules/team/api/team.api';
import { NON_ARCHIVED_TEAM_MEMBER_STATUSES } from '@/modules/team/types/dto/find-short-team-members.request';
import { FilterConfig, FilterOption, RangeMode } from '@/types/filters';

import { ProjectChangeType } from '../types/project-changes';
import {
  PROJECTS_FILTER_KEY_BILLING_MODEL,
  PROJECTS_FILTER_KEY_MANAGER,
  PROJECTS_FILTER_KEY_PROBABILITY,
  PROJECTS_FILTER_KEY_SEARCH,
  PROJECTS_FILTER_KEY_STATUS,
} from './projects-filter-keys';

export * from './rate-overview-table-columns';

export const getProjectsFiltersConfig = (): TableFilterConfig[] => [
  {
    type: 'search',
    name: PROJECTS_FILTER_KEY_SEARCH,
    label: i18n.t('common:search'),
    placeholder: i18n.t('authenticated.projects:searchPlaceholder'),
  },
  {
    type: 'autocomplete',
    name: PROJECTS_FILTER_KEY_STATUS,
    label: i18n.t('authenticated.projects:filters.status'),
    displayDot: true,
    options: [
      {
        label: i18n.t('authenticated.projects:status.active'),
        value: ProjectStatus.Active,
        color: 'green-600',
      },
      {
        label: i18n.t('authenticated.projects:status.tentative'),
        value: ProjectStatus.Tentative,
        color: 'orange-600',
      },
      {
        label: i18n.t('authenticated.projects:status.archived'),
        value: ProjectStatus.Archived,
        color: 'muted-foreground',
      },
    ],
  },
  {
    type: 'autocomplete',
    name: PROJECTS_FILTER_KEY_BILLING_MODEL,
    label: i18n.t('authenticated.projects:filters.billingModel'),
    options: [
      {
        label: i18n.t('authenticated.projects:filters.timeMaterial'),
        value: ProjectBillingModel.TM,
      },
      {
        label: i18n.t('authenticated.projects:filters.fixedPrice'),
        value: ProjectBillingModel.FixedPrice,
      },
      {
        label: i18n.t('authenticated.projects:filters.nonBillable'),
        value: ProjectBillingModel.NonBillable,
      },
    ],
  },
  {
    type: 'rangeMode',
    name: PROJECTS_FILTER_KEY_PROBABILITY,
    label: i18n.t('authenticated.projects:filters.probability'),
    placeholder: i18n.t('authenticated.projects:filters.eg50'),
    modes: [
      {
        label: i18n.t('authenticated.projects:filters.moreThan'),
        value: RangeMode.More,
      },
      {
        label: i18n.t('authenticated.projects:filters.lessThan'),
        value: RangeMode.Less,
      },
    ],
    unit: '%',
    max: 100,
  },
  {
    type: 'dynamic-autocomplete',
    name: PROJECTS_FILTER_KEY_MANAGER,
    label: i18n.t('authenticated.projects:filters.manager'),
    withSearch: true,
    loadOptions: async params =>
      getShortTeamMembers({
        filter: {
          isManager: true,
          searchString: params.searchString,
          statuses: NON_ARCHIVED_TEAM_MEMBER_STATUSES,
        },
        pagination: { cursor: params.cursor, pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES },
      }),
    renderLabel: (value: string, option: FilterOption) => (
      <div className="flex items-center gap-2">
        <EntityAvatar image={option.avatar} name={value} size="xs" shape="circle" />
        <span className="text-sm">{value}</span>
      </div>
    ),
    minSearchChars: 2,
    debounceMs: 250,
  },
];

export const getProjectsChangesTabFiltersConfig = (): FilterConfig[] => [
  {
    type: 'autocomplete',
    name: 'changeType',
    label: i18n.t('authenticated.projects:details.tabs.changes.filters.changeType'),
    options: [
      {
        label: i18n.t('authenticated.projects:details.tabs.changes.info'),
        value: ProjectChangeType.Info,
      },
      {
        label: i18n.t('authenticated.projects:details.tabs.changes.billing'),
        value: ProjectChangeType.Billing,
      },
      {
        label: i18n.t('authenticated.projects:details.tabs.changes.integration'),
        value: ProjectChangeType.Integration,
      },
    ],
  },

  {
    type: 'dateRange',
    name: 'dateRange',
    label: i18n.t('authenticated.projects:details.tabs.changes.filters.dateRange'),
  },
  {
    type: 'dynamic-autocomplete-paginated',
    name: 'changedBy',
    label: i18n.t('authenticated.projects:details.tabs.changes.filters.changedBy'),
    withSearch: true,
    loadItems: async (params: {
      searchString?: string;
      cursor?: number | null;
      [key: string]: any;
    }) => {
      const response = await getShortTeamMembers({
        filter: params.searchString ? { searchString: params.searchString } : undefined,
        pagination: {
          pageSize: DEFAULT_PAGE_SIZE_PROJECTS_SHORT_ENTITIES,
          cursor: params.cursor,
        },
      });
      return {
        items: response.items.map(item => ({
          id: item.id,
          name: item.name,
          avatarLink: item.avatarLink,
        })),
        pagination: response.pagination,
      };
    },
    CustomLabelComponent: (value: string, opt: FilterOption) => (
      <div className="flex items-center gap-2">
        <EntityAvatar name={value} image={opt.avatar ?? undefined} size="xs" shape="circle" />
        <span className="text-sm">{value}</span>
      </div>
    ),
  },
];
